import React, { use } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

interface FormInputs {
    deleteReason: string | null;
}

interface DeleteSubjectObservationDialogProps {
    observationId: number;
    recordId: string;
    actionsCount: number;
    reviewLogsCount: number;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean | undefined) => void;
}

const DeleteSubjectObservationDialog = ({
    observationId,
    onDeleteHandler,
    studyRef,
    recordId,
    actionsCount,
    reviewLogsCount,
    onClose,
}: DeleteSubjectObservationDialogProps) => {
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { t } = useTranslation(['common']);

    const smActiveReviewContext = use(SMActiveReviewContext);

    const { trigger: deleteObservation, isMutating: isDeleting } = SubjectObservationsApi.useDelete(
        studyRef,
        smActiveReviewContext?.state?.sessionId,
    );

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: '',
        },
    });

    const warningText = (
        <span className={cn('k-text-left', style.fontSize)}>
            {mtkTranslation('deleteObservationDialog.deleteObservationWarning1')}
            <span className={style.textHighLight}>
                {actionsCount} {mtkTranslation('deleteObservationDialog.deleteObservationWarning2')}
            </span>
            {mtkTranslation('deleteObservationDialog.deleteObservationWarning3')}
            <span className={styles.textHighLight}>
                {reviewLogsCount} {mtkTranslation('deleteObservationDialog.deleteObservationWarning4')}
            </span>
            {mtkTranslation('deleteObservationDialog.deleteObservationWarning5')}
        </span>
    );

    const onDelete = async (data: FormInputs) => {
        try {
            const response = await deleteObservation({ deleteReason: data.deleteReason, observationId });
            onDeleteHandler(response);
        } catch {
            onDeleteHandler(false);
        }
    };

    const formDisabled = isSubmitting || isDeleting;

    return (
        <DraggableDialog
            dataTestId="delete-subject-observation"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={`${mtkTranslation('deleteObservationDialog.title')}: ${recordId}`}
            initialWidth={530}
            initialHeight={actionsCount > 0 || reviewLogsCount > 0 ? 280 : 230}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || formDisabled}
            secondaryBtnDisabled={formDisabled}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {(actionsCount > 0 || reviewLogsCount > 0) && <InfoField type="Warning" message={warningText} />}
                <TextAreaController
                    className={style.textAreaOverrides}
                    maxLength={3000}
                    control={control}
                    name="deleteReason"
                    required
                    data-testid="field-delete-observation"
                    label={`${mtkTranslation('deleteObservationDialog.reasonForDeletion')} *`}
                    readOnly={formDisabled}
                />
            </div>
        </DraggableDialog>
    );
};

export default DeleteSubjectObservationDialog;
